import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, FileText, Sun, Wifi } from "lucide-react";

import { parseVkycPublicResponse, vkycApi } from "../../services/vkyc";
import { logApiErrorToSentry } from "../remittance-beta/utils";
import {
  PublicKycLanding,
  PublicKycLoading,
  PublicKycStatusLayout,
} from "./PublicKycLayout";
import { vkycStyles as styles } from "./styles";

type PageStatus = "loading" | "ready" | "redirecting" | "expired" | "invalid" | "error";

const VERIFICATION_STEPS = [
  {
    title: "Aadhaar Digilocker",
    mobileTitle: "Aadhaar",
    description: "Verify identity securely via Digilocker",
  },
  {
    title: "vKYC video call",
    mobileTitle: "vKYC call",
    description: "Live video KYC with an agent",
  },
];

const KEEP_ITEMS = [
  {
    label: "Aadhaar number",
    hint: "Linked mobile number helps Digilocker",
    icon: FileText,
  },
  {
    label: "PAN card",
    hint: "Keep the physical card nearby",
    icon: CreditCard,
  },
  {
    label: "Quiet, well-lit space",
    hint: "Face the camera with even lighting",
    icon: Sun,
  },
  {
    label: "Stable internet",
    hint: "Do not switch networks mid-call",
    icon: Wifi,
  },
];

const EXPIRED_TIPS = [
  "Ask your agent/consultant to generate a new link",
  "Open it on the same device and browser",
  "Keep Aadhaar and PAN ready before you start",
];

const getErrorMessage = (error: any) =>
  error?.response?.data?.error?.type ||
  error?.response?.data?.error ||
  error?.response?.error?.type ||
  error?.response?.error ||
  error?.message ||
  "Unable to open the KYC link. Please contact support.";

const VkycLinkPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
  const [statusMessage, setStatusMessage] = useState<string>();
  const [captureLink, setCaptureLink] = useState<string>("");

  const loadVkycLink = useCallback(async () => {
    if (!token) {
      setPageStatus("invalid");
      setStatusMessage("This KYC link is invalid. Please request a new link.");
      return;
    }

    setPageStatus("loading");

    try {
      const response = await vkycApi.getPublicVkyc(token);
      const data = parseVkycPublicResponse(response);

      if (!data) {
        setPageStatus("error");
        setStatusMessage("Unable to open the KYC link. Please try again.");
        return;
      }

      if (data.expired || data.valid === false) {
        setPageStatus("expired");
        return;
      }

      if (data.completed) {
        window.location.replace("/kyc/confirmation");
        return;
      }

      if (data.captureLink) {
        setCaptureLink(data.captureLink);
        setPageStatus("ready");
        return;
      }

      setPageStatus("error");
      setStatusMessage(data.message || "KYC link is not available right now. Please try again.");
    } catch (error: any) {
      logApiErrorToSentry(error, "vkycApi.getPublicVkyc");
      setPageStatus("error");
      setStatusMessage(getErrorMessage(error));
    }
  }, [token]);

  useEffect(() => {
    loadVkycLink();
  }, [loadVkycLink]);

  const handleStartKyc = () => {
    if (!captureLink) return;
    setPageStatus("redirecting");
    window.location.href = captureLink;
  };

  if (pageStatus === "loading") {
    return <PublicKycLoading message="Verifying your KYC link..." />;
  }

  if (pageStatus === "expired") {
    return (
      <PublicKycStatusLayout
        contextTitle="This link is no longer active"
        contextBody="KYC links expire within 24 hours to keep your details safe. Please request a new link and complete your KYC at the earliest."
        tips={EXPIRED_TIPS}
        tone="expired"
        statusTitle="Link expired"
        statusDescription="Your KYC link has expired. Please request a new link from your agent or consultant to continue."
      />
    );
  }

  if (pageStatus === "invalid") {
    return (
      <PublicKycStatusLayout
        contextTitle="This link is no longer active"
        contextBody="This KYC link is invalid. Please request a new link from your agent or consultant and complete your KYC at the earliest."
        tips={EXPIRED_TIPS}
        tone="error"
        statusTitle="Invalid link"
        statusDescription={statusMessage}
      />
    );
  }

  if (pageStatus === "error") {
    return (
      <PublicKycStatusLayout
        contextTitle="We could not open this KYC"
        contextBody="Something went wrong while opening your verification link. Please try again, or request a new link from your agent or consultant."
        tips={EXPIRED_TIPS}
        tone="error"
        statusTitle="Something went wrong"
        statusDescription={statusMessage}
        action={
          <button type="button" onClick={loadVkycLink} className={styles.retryBtn}>
            Try again
          </button>
        }
      />
    );
  }

  return (
    <PublicKycLanding
      eyebrow="VIDEO KYC"
      title="Complete your verification"
      subtitleDesktop="Two steps, about 2-3 minutes. Keep Aadhaar and PAN ready before you start."
      subtitleCompact="Two steps, about 2-3 minutes. Keep Aadhaar and PAN ready."
      steps={VERIFICATION_STEPS}
      keepItems={KEEP_ITEMS}
      ctaLabel={pageStatus === "redirecting" ? "Redirecting..." : "Accept and Proceed"}
      ctaBusy={pageStatus === "redirecting"}
      ctaDisabled={!captureLink}
      onCta={handleStartKyc}
    />
  );
};

export default VkycLinkPage;
